/**
 * SelectionManager - Text selection and highlighting management for ClientFirstDocumentEditor.tsx
 * 
 * Provides professional text selection behavior with visual highlighting,
 * supporting copy/paste operations and legal document review workflows.
 * 
 * ClientFirst Integration:
 * - Managed by InputManager within ClientFirstDocumentEditor.tsx
 * - Provides real-time selection feedback through the active rendering pipeline
 * - Coordinates with server-authoritative layout via SyncManager
 * - Supports legal document workflows (annotations, citations, compliance)
 * - Integrates with CaretManager for seamless text navigation
 * 
 * Architecture: Part of the ClientFirst input management system that provides
 * immediate visual feedback while maintaining server layout authority.
 */

import { LocalDocumentEngine } from '../core/LocalDocumentEngine';
import { TextOperations } from '../core/TextOperations';
import { SelectionRange, TextRange, Rectangle } from '../types/DocumentTypes';

// Minimal renderer interface for selection management
interface MinimalRenderer {
  getCanvas(): HTMLCanvasElement;
  getContext(): CanvasRenderingContext2D;
  requestRender(): void;
}

// Layout information interface for accurate selection rendering
interface LayoutInfo {
  paperDimensions: {
    contentX: number;
    contentY: number;
    contentWidth: number;
    contentHeight: number;
    margins: {
      left: number;
      right: number;
      top: number;
      bottom: number;
    };
  };
  currentBackendLayoutPlan?: any;
  fontMetrics?: {
    charWidth: number;
    lineHeight: number;
    fontSize: number;
  };
}

export class SelectionManager {
  protected documentEngine: LocalDocumentEngine;
  protected renderer: MinimalRenderer;
  protected layoutInfo: LayoutInfo | null = null;
  
  // Selection state
  private currentSelection: SelectionRange | null = null;
  public isSelecting: boolean = false;
  private selectionAnchor: number = 0; // Starting point of selection
  
  // Visual properties
  private selectionColor: string = 'rgba(0, 123, 255, 0.3)';
  private selectionBorderColor: string = 'rgba(0, 123, 255, 0.8)';
  
  // Context menu support
  private contextMenuCallback?: (event: any) => void;

  constructor(documentEngine: LocalDocumentEngine, renderer: MinimalRenderer, layoutInfo?: LayoutInfo) {
    this.documentEngine = documentEngine;
    this.renderer = renderer;
    this.layoutInfo = layoutInfo || null;
    
    // Listen for document changes to update selection
    this.documentEngine.on('textChanged', () => this.validateSelection());
  }

  // ============================================================================
  // LAYOUT MANAGEMENT
  // ============================================================================

  /**
   * Update layout information for accurate selection rendering
   */
  updateLayoutInfo(layoutInfo: LayoutInfo): void {
    this.layoutInfo = layoutInfo;
    console.log('[SelectionManager] Layout info updated:', layoutInfo);
  }

  // ============================================================================
  // SELECTION OPERATIONS
  // ============================================================================

  /**
   * Start text selection at position
   */
  startSelection(position: number): void {
    const safePosition = Math.max(0, Math.min(position, this.documentEngine.getText().length));
    
    this.isSelecting = true;
    this.selectionAnchor = safePosition;
    this.currentSelection = {
      start: safePosition,
      end: safePosition,
      direction: 'forward'
    };
    
    console.log(`[SelectionManager] Started selection at position ${safePosition}`);
    
    // Trigger canvas update
    this.renderer.requestRender();
  }

  /**
   * Extend selection to new position
   */
  extendSelection(position: number): void {
    if (!this.isSelecting || this.currentSelection === null) return;
    
    const safePosition = Math.max(0, Math.min(position, this.documentEngine.getText().length));
    
    // Update selection range
    if (safePosition >= this.selectionAnchor) {
      // Forward selection
      this.currentSelection = {
        start: this.selectionAnchor,
        end: safePosition,
        direction: 'forward'
      };
    } else {
      // Backward selection
      this.currentSelection = {
        start: safePosition,
        end: this.selectionAnchor,
        direction: 'backward'
      };
    }
    
    // Update document engine selection
    this.documentEngine.selectRange(this.currentSelection.start, this.currentSelection.end);
    
    console.log(`[SelectionManager] Extended selection: ${this.currentSelection.start}-${this.currentSelection.end}`);
    
    // Trigger canvas update
    this.renderer.requestRender();
  }

  /**
   * End selection at position
   */
  endSelection(position: number): void {
    if (!this.isSelecting) return;
    
    this.extendSelection(position);
    this.isSelecting = false;
    
    // Clear selection if start equals end
    if (this.currentSelection && this.currentSelection.start === this.currentSelection.end) {
      this.clearSelection();
    }
    
    console.log('[SelectionManager] Ended selection');
    
    // Trigger canvas update
    this.renderer.requestRender();
  }

  /**
   * Set selection range directly
   */
  setSelection(start: number, end: number): void {
    const text = this.documentEngine.getText();
    const safeStart = Math.max(0, Math.min(start, text.length));
    const safeEnd = Math.max(safeStart, Math.min(end, text.length));
    
    if (safeStart === safeEnd) {
      this.clearSelection();
      return;
    }
    
    this.currentSelection = {
      start: safeStart,
      end: safeEnd,
      direction: start <= end ? 'forward' : 'backward'
    };
    
    this.documentEngine.selectRange(safeStart, safeEnd);
    
    console.log(`[SelectionManager] Set selection: ${safeStart}-${safeEnd}`);
    
    // Trigger canvas update
    this.renderer.requestRender();
  }

  /**
   * Clear current selection
   */
  clearSelection(): void {
    this.currentSelection = null;
    this.isSelecting = false;
    this.documentEngine.clearSelection();
    
    console.log('[SelectionManager] Cleared selection');
    
    // Trigger canvas update
    this.renderer.requestRender();
  }

  // ============================================================================
  // SMART SELECTION
  // ============================================================================

  /**
   * Select word at position
   */
  selectWordAt(position: number): void {
    const text = this.documentEngine.getText();
    const wordBoundaries = TextOperations.findWordBoundaries(text, position);
    
    this.setSelection(wordBoundaries.start, wordBoundaries.end);
  }

  /**
   * Select line at position
   */
  selectLineAt(position: number): void {
    const text = this.documentEngine.getText();
    const lineBoundaries = TextOperations.findLineBoundaries(text, position);
    
    this.setSelection(lineBoundaries.start, lineBoundaries.end);
  }

  /**
   * Select paragraph at position
   */
  selectParagraphAt(position: number): void {
    const text = this.documentEngine.getText();
    
    // Find paragraph boundaries (double newlines or document boundaries)
    let start = position;
    let end = position;
    
    // Move start backward to paragraph beginning
    while (start > 0) {
      if (text[start - 1] === '\n' && (start === 1 || text[start - 2] === '\n')) {
        break; // Found paragraph break
      }
      start--;
    }
    
    // Move end forward to paragraph end
    while (end < text.length) {
      if (text[end] === '\n' && (end === text.length - 1 || text[end + 1] === '\n')) {
        end++; // Include the newline
        break;
      }
      end++;
    }
    
    this.setSelection(start, end);
  }

  /**
   * Select all text
   */
  selectAll(): void {
    const text = this.documentEngine.getText();
    this.setSelection(0, text.length);
  }

  // ============================================================================
  // LEGAL DOCUMENT SELECTIONS
  // ============================================================================

  /**
   * Select legal citation at position
   */
  selectCitationAt(position: number): boolean {
    const text = this.documentEngine.getText();
    const citations = TextOperations.findLegalCitations(text);
    
    const citation = citations.find(c => position >= c.start && position <= c.end);
    
    if (citation) {
      this.setSelection(citation.start, citation.end);
      return true;
    }
    
    return false;
  }

  /**
   * Select contract clause at position
   */
  selectClauseAt(position: number): boolean {
    const text = this.documentEngine.getText();
    const clauses = TextOperations.findContractClauses(text);
    
    const clause = clauses.find(c => position >= c.start && position <= c.end);
    
    if (clause) {
      this.setSelection(clause.start, clause.end);
      return true;
    }
    
    return false;
  }

  // ============================================================================
  // SELECTION QUERIES
  // ============================================================================

  /**
   * Check if there is an active selection
   */
  hasSelection(): boolean {
    return this.currentSelection !== null && 
           this.currentSelection.start !== this.currentSelection.end;
  }

  /**
   * Check if currently selecting (mouse drag in progress)
   */
  isCurrentlySelecting(): boolean {
    return this.isSelecting;
  }

  /**
   * Get current selection range
   */
  getSelection(): SelectionRange | null {
    return this.currentSelection;
  }

  /**
   * Get selected text
   */
  getSelectedText(): string {
    if (!this.hasSelection() || !this.currentSelection) return '';
    
    const text = this.documentEngine.getText();
    return text.slice(this.currentSelection.start, this.currentSelection.end);
  }

  /**
   * Get selection length
   */
  getSelectionLength(): number {
    if (!this.hasSelection() || !this.currentSelection) return 0;
    
    return this.currentSelection.end - this.currentSelection.start;
  }

  // ============================================================================
  // SELECTION VALIDATION
  // ============================================================================

  /**
   * Validate selection after document changes
   */
  private validateSelection(): void {
    if (!this.currentSelection) return;
    
    const textLength = this.documentEngine.getText().length;
    
    // Clamp selection to document bounds
    const start = Math.max(0, Math.min(this.currentSelection.start, textLength));
    const end = Math.max(start, Math.min(this.currentSelection.end, textLength));
    
    if (start !== this.currentSelection.start || end !== this.currentSelection.end) {
      console.log(`[SelectionManager] Adjusted selection bounds: ${this.currentSelection.start}-${this.currentSelection.end} → ${start}-${end}`);
      
      this.currentSelection = {
        start,
        end,
        direction: this.currentSelection.direction
      };
      
      this.documentEngine.selectRange(start, end);
    }
  }

  // ============================================================================
  // SELECTION RENDERING
  // ============================================================================

  /**
   * Render selection highlight
   */
  renderSelection(): void {
    if (!this.hasSelection() || !this.currentSelection) return;
    
    const selectionRects = this.calculateSelectionRects(this.currentSelection);
    const ctx = this.renderer.getContext();
    
    // Render selection background
    ctx.fillStyle = this.selectionColor;
    for (const rect of selectionRects) {
      ctx.fillRect(rect.x, rect.y, rect.width, rect.height);
    }
    
    // Render selection border
    ctx.strokeStyle = this.selectionBorderColor;
    ctx.lineWidth = 1;
    for (const rect of selectionRects) {
      ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
    }
  }

  protected calculateSelectionRects(selection: SelectionRange): Rectangle[] {
    const rects: Rectangle[] = [];
    
    // Use actual layout data if available, otherwise fall back to reasonable defaults
    if (this.layoutInfo) {
      return this.calculateSelectionRectsWithLayout(selection);
    } else {
      console.warn('[SelectionManager] No layout info available, using fallback calculation');
      return this.calculateSelectionRectsFallback(selection);
    }
  }

  /**
   * Calculate selection rectangles using actual layout data
   */
  private calculateSelectionRectsWithLayout(selection: SelectionRange): Rectangle[] {
    const rects: Rectangle[] = [];
    
    if (!this.layoutInfo) return rects;
    
    const { paperDimensions, fontMetrics, currentBackendLayoutPlan } = this.layoutInfo;
    
    // Use actual paper dimensions
    const contentX = paperDimensions.contentX || paperDimensions.margins.left;
    const contentY = paperDimensions.contentY || paperDimensions.margins.top;
    const contentWidth = paperDimensions.contentWidth;
    
    // Use actual font metrics or reasonable defaults
    const charWidth = fontMetrics?.charWidth || 8; // More reasonable default
    const lineHeight = fontMetrics?.lineHeight || 18; // Increased to better match actual font height
    const charsPerLine = Math.floor(contentWidth / charWidth);
    
    console.log('[SelectionManager] Using layout data:', {
      contentX,
      contentY,
      contentWidth,
      charWidth,
      lineHeight,
      charsPerLine,
      selection: `${selection.start}-${selection.end}`
    });
    
    const startLine = Math.floor(selection.start / charsPerLine);
    const endLine = Math.floor(selection.end / charsPerLine);
    
    if (startLine === endLine) {
      // Single line selection
      const startCol = selection.start % charsPerLine;
      const endCol = selection.end % charsPerLine;
      
      rects.push({
        x: contentX + (startCol * charWidth),
        y: contentY + (startLine * lineHeight),
        width: (endCol - startCol) * charWidth,
        height: lineHeight
      });
    } else {
      // Multi-line selection
      const startCol = selection.start % charsPerLine;
      const endCol = selection.end % charsPerLine;
      
      // First line
      rects.push({
        x: contentX + (startCol * charWidth),
        y: contentY + (startLine * lineHeight),
        width: (charsPerLine - startCol) * charWidth,
        height: lineHeight
      });
      
      // Middle lines
      for (let line = startLine + 1; line < endLine; line++) {
        rects.push({
          x: contentX,
          y: contentY + (line * lineHeight),
          width: charsPerLine * charWidth,
          height: lineHeight
        });
      }
      
      // Last line
      if (endCol > 0) {
        rects.push({
          x: contentX,
          y: contentY + (endLine * lineHeight),
          width: endCol * charWidth,
          height: lineHeight
        });
      }
    }
    
    return rects;
  }

  /**
   * Fallback calculation when layout data is not available
   */
  private calculateSelectionRectsFallback(selection: SelectionRange): Rectangle[] {
    const rects: Rectangle[] = [];
    
    // Use more reasonable defaults based on typical document settings
    const charWidth = 8; // More realistic character width
    const lineHeight = 18; // Increased to better match actual font height
    const leftMargin = 72; // 1 inch margin
    const topMargin = 72; // 1 inch margin
    const charsPerLine = 80; // Reasonable line length
    
    const startLine = Math.floor(selection.start / charsPerLine);
    const endLine = Math.floor(selection.end / charsPerLine);
    
    if (startLine === endLine) {
      // Single line selection
      const startCol = selection.start % charsPerLine;
      const endCol = selection.end % charsPerLine;
      
      rects.push({
        x: leftMargin + (startCol * charWidth),
        y: topMargin + (startLine * lineHeight),
        width: (endCol - startCol) * charWidth,
        height: lineHeight
      });
    } else {
      // Multi-line selection
      const startCol = selection.start % charsPerLine;
      const endCol = selection.end % charsPerLine;
      
      // First line
      rects.push({
        x: leftMargin + (startCol * charWidth),
        y: topMargin + (startLine * lineHeight),
        width: (charsPerLine - startCol) * charWidth,
        height: lineHeight
      });
      
      // Middle lines
      for (let line = startLine + 1; line < endLine; line++) {
        rects.push({
          x: leftMargin,
          y: topMargin + (line * lineHeight),
          width: charsPerLine * charWidth,
          height: lineHeight
        });
      }
      
      // Last line
      if (endCol > 0) {
        rects.push({
          x: leftMargin,
          y: topMargin + (endLine * lineHeight),
          width: endCol * charWidth,
          height: lineHeight
        });
      }
    }
    
    return rects;
  }

  // ============================================================================
  // SELECTION CUSTOMIZATION
  // ============================================================================

  /**
   * Set selection appearance
   */
  setSelectionAppearance(options: {
    color?: string;
    borderColor?: string;
    opacity?: number;
  }): void {
    if (options.color) {
      this.selectionColor = options.color;
    }
    
    if (options.borderColor) {
      this.selectionBorderColor = options.borderColor;
    }
    
    if (options.opacity !== undefined) {
      // Update color with new opacity
      const rgb = this.hexToRgb(this.selectionColor.replace(/rgba?\([^)]+\)/, ''));
      if (rgb) {
        this.selectionColor = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${options.opacity})`;
      }
    }
    
    // Re-render if selection is active
    if (this.hasSelection()) {
      this.renderSelection();
    }
  }

  private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  }

  // ============================================================================
  // CONTEXT MENU SUPPORT
  // ============================================================================

  /**
   * Set callback for context menu events
   */
  onContextMenu(callback: (event: any) => void): void {
    this.contextMenuCallback = callback;
    console.log('[SelectionManager] Context menu callback registered');
  }

  /**
   * Handle right-click events
   */
  handleRightClick(event: MouseEvent): boolean {
    console.log('🖱️ [SelectionManager] Right-click detected', {
      hasSelection: this.hasSelection(),
      hasCallback: !!this.contextMenuCallback,
      selection: this.currentSelection
    });

    if (!this.hasSelection() || !this.contextMenuCallback) {
      console.log('⚠️ [SelectionManager] Cannot show context menu:', {
        hasSelection: this.hasSelection(),
        hasCallback: !!this.contextMenuCallback
      });
      return false;
    }

    const selection = this.getSelection();
    if (!selection) return false;

    const selectedText = this.getSelectedText();

    // Create context menu event
    const contextMenuEvent = {
      position: { x: event.clientX, y: event.clientY },
      selectedText,
      selection,
      currentFormat: {} // Will be enhanced with actual format detection
    };

    // Prevent browser context menu
    event.preventDefault();
    event.stopPropagation();

    // Trigger callback
    this.contextMenuCallback(contextMenuEvent);
    console.log('✅ [SelectionManager] Context menu triggered');
    
    return true;
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Destroy selection manager and cleanup resources
   */
  destroy(): void {
    this.clearSelection();
    console.log('[SelectionManager] Destroyed');
  }
}
